(() => {
  const PROXY_URL = '/apps/chubbytrend';

  /*
   * Tracks which customer owns the Wishlist currently
   * stored in this browser.
   */
  const ACCOUNT_STORAGE_KEY =
    'ct_wishlist_account_v1';

  const SYNC_EVENT =
    'ct:wishlist:sync-state';

  const SAVE_DELAY = 450;
  const MAX_WISHLIST_ITEMS = 200;

  let initialized = false;
  let loggedIn = false;
  let customerId = null;
  let applyingServerState = false;
  let saveTimer = null;

  /*
   * Ensures POST requests execute sequentially.
   * This prevents an older save from overwriting
   * a newer save when the customer clicks quickly.
   */
  let saveQueue = Promise.resolve();

  function sanitizeHandles(value) {
    if (!Array.isArray(value)) {
      return [];
    }

    return [
      ...new Set(
        value
          .filter(
            (handle) =>
              typeof handle === 'string'
          )
          .map((handle) =>
            handle.trim().toLowerCase()
          )
          .filter(
            (handle) =>
              handle.length > 0 &&
              handle.length <= 255 &&
              /^[a-z0-9][a-z0-9-]*$/.test(
                handle
              )
          )
      ),
    ].slice(0, MAX_WISHLIST_ITEMS);
  }

  function arraysMatch(first, second) {
    const firstHandles =
      sanitizeHandles(first);

    const secondHandles =
      sanitizeHandles(second);

    if (
      firstHandles.length !==
      secondHandles.length
    ) {
      return false;
    }

    return firstHandles.every(
      (handle, index) =>
        handle === secondHandles[index]
    );
  }

  function readLocalWishlist() {
    if (
      window.CTWishlist &&
      typeof window.CTWishlist.getHandles ===
        'function'
    ) {
      return sanitizeHandles(
        window.CTWishlist.getHandles()
      );
    }

    try {
      const storedValue =
        window.localStorage.getItem(
          'ct_wishlist_v1'
        );

      return sanitizeHandles(
        storedValue
          ? JSON.parse(storedValue)
          : []
      );
    } catch (error) {
      console.warn(
        'Chubbytrend Wishlist Sync: Could not read Local Storage.',
        error
      );

      return [];
    }
  }

  function replaceLocalWishlist(handles) {
    const cleanHandles =
      sanitizeHandles(handles);

    applyingServerState = true;

    try {
      if (
        window.CTWishlist &&
        typeof window.CTWishlist.replace ===
          'function'
      ) {
        window.CTWishlist.replace(
          cleanHandles
        );

        return;
      }

      window.localStorage.setItem(
        'ct_wishlist_v1',
        JSON.stringify(cleanHandles)
      );

      document.dispatchEvent(
        new CustomEvent(
          'ct:wishlist:updated',
          {
            detail: {
              handles: cleanHandles,
              count: cleanHandles.length,
            },
          }
        )
      );
    } finally {
      /*
       * ct:wishlist:updated is dispatched
       * synchronously. Release the guard after
       * that event has completed.
       */
      window.queueMicrotask(() => {
        applyingServerState = false;
      });
    }
  }

  function emitSyncState(
    state,
    extraDetail = {}
  ) {
    document.dispatchEvent(
      new CustomEvent(SYNC_EVENT, {
        detail: {
          state,
          loggedIn,
          customerId,
          ...extraDetail,
        },
      })
    );
  }

  async function requestProxy(
    method = 'GET',
    body = null
  ) {
    const options = {
      method,
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    };

    if (body !== null) {
      options.headers['Content-Type'] =
        'application/json';

      options.body = JSON.stringify(body);
    }

    const response = await fetch(
      PROXY_URL,
      options
    );

    const responseText =
      await response.text();

    let responseData = null;

    try {
      responseData = responseText
        ? JSON.parse(responseText)
        : null;
    } catch (error) {
      throw new Error(
        `Wishlist API returned an invalid response (${response.status}).`
      );
    }

    if (!response.ok) {
      throw new Error(
        responseData?.error ||
          `Wishlist API request failed (${response.status}).`
      );
    }

    return responseData;
  }

  async function saveRemoteWishlist(
    handles
  ) {
    if (!loggedIn || !customerId) {
      return;
    }

    const cleanHandles =
      sanitizeHandles(handles);

    emitSyncState('saving', {
      count: cleanHandles.length,
    });

    const result = await requestProxy(
      'POST',
      {
        handles: cleanHandles,
      }
    );

    if (!result?.ok) {
      throw new Error(
        result?.error ||
          'Wishlist could not be saved.'
      );
    }

    emitSyncState('synced', {
      count: cleanHandles.length,
      handles: cleanHandles,
    });
  }

  function queueRemoteSave(
    handles = readLocalWishlist()
  ) {
    if (
      !initialized ||
      !loggedIn ||
      !customerId ||
      applyingServerState
    ) {
      return;
    }

    const cleanHandles =
      sanitizeHandles(handles);

    window.clearTimeout(saveTimer);

    saveTimer = window.setTimeout(() => {
      saveQueue = saveQueue
        .then(() =>
          saveRemoteWishlist(cleanHandles)
        )
        .catch((error) => {
          console.error(
            'Chubbytrend Wishlist Sync:',
            error
          );

          emitSyncState('error', {
            message: error.message,
          });
        });
    }, SAVE_DELAY);
  }

  async function initializeSync() {
    initialized = false;

    emitSyncState('loading');

    let remoteData;

    try {
      remoteData =
        await requestProxy('GET');
    } catch (error) {
      console.warn(
        'Chubbytrend Wishlist Sync: Server is unavailable. Continuing with Local Storage.',
        error
      );

      initialized = true;

      emitSyncState('local-only', {
        count:
          readLocalWishlist().length,
        message: error.message,
      });

      return;
    }

    /*
     * Guest customer:
     * Continue using Local Storage.
     */
    if (!remoteData?.loggedIn) {
      loggedIn = false;
      customerId = null;

      const previousAccountId =
        window.localStorage.getItem(
          ACCOUNT_STORAGE_KEY
        );

      /*
       * Prevent a signed-out visitor from seeing
       * the previous customer's saved products
       * on a shared device.
       */
      if (previousAccountId) {
        replaceLocalWishlist([]);

        window.localStorage.removeItem(
          ACCOUNT_STORAGE_KEY
        );
      }

      initialized = true;

      emitSyncState('guest', {
        count:
          readLocalWishlist().length,
      });

      return;
    }

    loggedIn = true;
    customerId = String(
      remoteData.customerId
    );

    const remoteHandles =
      sanitizeHandles(
        remoteData.handles
      );

    const localHandles =
      readLocalWishlist();

    const previousAccountId =
      window.localStorage.getItem(
        ACCOUNT_STORAGE_KEY
      );

    /*
     * On the first login, Local Storage contains
     * the guest customer's Wishlist. Merge it once
     * into the remote Wishlist so those products
     * are not lost during sign-in.
     *
     * On later page loads, the account marker shows
     * that Local Storage is only a cache for this
     * customer. The server then remains the source
     * of truth and stale local data is not merged.
     *
     * If another account used this browser, never
     * merge that account's local cache into the
     * current customer.
     */
    let synchronizedHandles =
      remoteHandles;

    if (
      !previousAccountId &&
      localHandles.length > 0
    ) {
      const mergedHandles =
        sanitizeHandles([
          ...remoteHandles,
          ...localHandles,
        ]);

      if (
        !arraysMatch(
          remoteHandles,
          mergedHandles
        )
      ) {
        try {
          await saveRemoteWishlist(
            mergedHandles
          );
        } catch (error) {
          console.error(
            'Chubbytrend Wishlist Sync: Could not merge the guest Wishlist.',
            error
          );

          initialized = true;

          emitSyncState('error', {
            count:
              localHandles.length,
            message: error.message,
          });

          return;
        }
      }

      synchronizedHandles =
        mergedHandles;
    }

    window.localStorage.setItem(
      ACCOUNT_STORAGE_KEY,
      customerId
    );

    if (
      !arraysMatch(
        localHandles,
        synchronizedHandles
      )
    ) {
      replaceLocalWishlist(
        synchronizedHandles
      );
    }

    initialized = true;

    emitSyncState('synced', {
      count:
        synchronizedHandles.length,
      handles:
        synchronizedHandles,
    });
  }

  /*
   * Every normal Wishlist add/remove operation
   * already dispatches this event.
   */
  document.addEventListener(
    'ct:wishlist:updated',
    (event) => {
      const handles =
        event.detail?.handles ||
        readLocalWishlist();

      queueRemoteSave(handles);
    }
  );

  document.addEventListener(
    'DOMContentLoaded',
    () => {
      initializeSync();
    }
  );

  /*
   * Recheck identity when a page is restored
   * from the browser back-forward cache.
   */
  window.addEventListener(
    'pageshow',
    (event) => {
      if (event.persisted) {
        initializeSync();
      }
    }
  );

  window.CTWishlistAccountSync = {
    refresh() {
      return initializeSync();
    },

    getState() {
      return {
        initialized,
        loggedIn,
        customerId,
        handles:
          readLocalWishlist(),
      };
    },
  };
})();
